import { type Message } from '../MessageCard/types'

type MessageMetadata = Pick<Message, 'id' | 'messageType'>

/** Avoids subpixel overshoot that paints a scrollbar with scrollTopMax === 0. */
export const PIN_SPACER_SUBPIXEL_SLACK_PX = 1

export const getScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null
  while (node != null && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node
    }
    node = node.parentElement
  }
  return null
}

export const getLastUserMessageIndex = (messages: MessageMetadata[]): number => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].messageType === 'user') {
      return index
    }
  }
  return -1
}

export const getPixelValue = (value: string): number => {
  const parsed = Number.parseFloat(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

export const getGapPixelValue = (rowGap: string, gap: string): number => {
  const parsed = Number.parseFloat(rowGap !== '' ? rowGap : gap)
  return Number.isFinite(parsed) ? parsed : 0
}

export const getFlexGapPx = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element)
  return getGapPixelValue(style.rowGap, style.gap)
}

export const findNewlySentUserMessage = <T extends MessageMetadata>(
  messages: T[],
  previousIds: Set<string>
): T | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message.messageType !== 'user' || previousIds.has(message.id)) {
      continue
    }

    const isLatest = index === messages.length - 1
    const isFollowedByAssistantOnly = index === messages.length - 2 && messages[index + 1]?.messageType === 'copilot'
    if (isLatest || isFollowedByAssistantOnly) {
      return message
    }
  }

  return null
}

export const countNewMessages = <T extends MessageMetadata>(messages: T[], previousIds: Set<string>): number =>
  messages.reduce((count, message) => (previousIds.has(message.id) ? count : count + 1), 0)

/**
 * Message content (pin spacer already removed) is taller than the scrollport once
 * `scroll-padding-top` is used space. That inset matches the wrapper `paddingTop`
 * above the list, which adds height the list's own scrollHeight does not include.
 * The extra pixel ignores a single subpixel, same as the unpadded comparison.
 */
export const isContentOverflowingScrollport = (
  contentHeight: number,
  clientHeight: number,
  scrollPaddingTop: number
): boolean => contentHeight > clientHeight - scrollPaddingTop + 1

interface GetPinSpacerHeightOptions {
  availableHeight: number
  userMessageHeight: number
  contentAfterUserHeight: number
  siblingCountAfterUser: number
  gap: number
  verticalPadding: number
}

/**
 * Viewport height left for pinning after `scroll-padding-top` (e.g. topnav inset).
 * Pair with {@link getPinSpacerHeight} so the spacer fills the inset scrollport, not the raw client box.
 */
export const getPinAvailableHeight = (clientHeight: number, scrollPaddingTop: number): number =>
  clientHeight - scrollPaddingTop

interface GetPinnedUserMessageScrollTopOptions {
  userTop: number
  parentTop: number
  scrollTop: number
  scrollPaddingTop: number
}

/**
 * Scroll offset that places the user message under `scroll-padding-top`, not flush with the scrollport edge.
 */
export const getPinnedUserMessageScrollTop = ({
  userTop,
  parentTop,
  scrollTop,
  scrollPaddingTop
}: GetPinnedUserMessageScrollTopOptions): number => Math.max(0, userTop - parentTop + scrollTop - scrollPaddingTop)

export const getPinSpacerHeight = ({
  availableHeight,
  userMessageHeight,
  contentAfterUserHeight,
  siblingCountAfterUser,
  gap,
  verticalPadding
}: GetPinSpacerHeightOptions): number => {
  // Gaps after each content sibling, plus the gap before the spacer itself.
  const gapsAfterUser = (siblingCountAfterUser + 1) * gap
  return Math.max(
    0,
    Math.floor(availableHeight - userMessageHeight - contentAfterUserHeight - gapsAfterUser - verticalPadding) -
      PIN_SPACER_SUBPIXEL_SLACK_PX
  )
}

/** Zero-height flex items still consume gap — hide instead of collapsing to 0px. */
export const setPinSpacerHeight = (spacer: HTMLElement, heightPx: number): void => {
  if (heightPx <= 0) {
    spacer.style.height = '0px'
    spacer.style.display = 'none'
    return
  }

  spacer.style.display = ''
  spacer.style.height = `${heightPx}px`
}
